import React from "react";
import { Box, Button, Flex, Image, Text, VStack, HStack } from "@chakra-ui/react"; // Claude: Added Image for LinkedIn photo 09-02-2026
import { useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import { Images } from "../../Assets/images";
import { MdPerson3 } from "react-icons/md";
import { BsFillPersonFill } from "react-icons/bs";

type Props = {
    personName: string;
    companyName: string;
    unreadNotifications: string;
    role: string;
    navigateUrl: string;
    profileNavigateUrl: string;
    linkedinPhoto?: string; // Claude: LinkedIn profile photo URL 09-02-2026
    verificationStatus?: string; // Claude: show negative status below name row 15-03-2026
    visibilityMode?: string; // Claude: seller profile visibility badge 27-03-2026
    isPlanActive?: boolean; // Claude: subscription expired warning 10-Apr-2026
    hasSubscriptionPlan?: boolean; // Claude: distinguishes no-plan vs expired — badge only when plan exists but inactive 20-04-2026
};

const Jumbotron = (props: Props) => {
    const navigate = useNavigate();

    return (
        <Flex
            alignItems={"center"}
            w={"100%"}
            bg={"primary.main"}
            bgImage={Images.headerElements}
            backgroundSize="cover"
            backgroundPosition="center"
            direction={"column"}
            // Claude: reduced padding 26-03-2026
            py={"12px"}
            px={"16px"}
        >
            {/* Claude: logo + role centered; name+company left-aligned below profile icon 23-02-2026 */}
            <HStack w={"100%"} justify={"space-between"} alignItems={"center"}>
                {/* Profile Icon */}
                <Button
                    w={"40px"}
                    h={"40px"}
                    borderRadius={props.linkedinPhoto ? "full" : "10px"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    bg={props.linkedinPhoto ? "transparent" : "secondary.orange"}
                    fontSize={"20px"}
                    p={0}
                    minW={"40px"}
                    overflow={"hidden"}
                    _hover={{ backgroundColor: props.linkedinPhoto ? "transparent" : "secondary.lightHover", opacity: props.linkedinPhoto ? 0.85 : 1 }}
                    onClick={() => { navigate(props.profileNavigateUrl); }}
                >
                    {props.linkedinPhoto ? (
                        <Image src={props.linkedinPhoto} alt="Profile" w={"40px"} h={"40px"} borderRadius={"full"} objectFit={"cover"} />
                    ) : (
                        <Flex width="22px" height="22px" align="center" justify="center">
                            <MdPerson3 color="white" fontSize="22px" />
                        </Flex>
                    )}
                </Button>

                {/* Centre: AFTR logo + role label */}
                {/* Claude: logo + role label + BETA 19-03-2026 */}
                <VStack spacing={0} flex={1} alignItems={"center"} justifyContent={"center"}>
                    <Image src={Images.AftrLogo} h={"48px"} objectFit={"contain"} />
                    <Text fontSize={"11px"} fontWeight={300} color={"whiteAlpha.600"} letterSpacing={"wide"}>
                        {props.role.toLowerCase()} (beta)
                    </Text>
                </VStack>

                {/* Notification button */}
                <Button
                    w={"40px"} h={"40px"} borderRadius={"10px"} display={"flex"}
                    alignItems={"center"} justifyContent={"center"}
                    bg={"secondary.orange"} position={"relative"}
                    _hover={{ backgroundColor: "secondary.lightHover" }}
                    onClick={() => { navigate(props.navigateUrl); }}
                >
                    <span><IoMdNotificationsOutline size={22} color={"white"} /></span>
                    <Box
                        position={"absolute"} top={-2} right={-2}
                        h={"14px"} w={"14px"} rounded={"full"}
                        bg={props.unreadNotifications && props.unreadNotifications != "0" ? "button.green" : "gray.500"}
                        fontSize={8} color={"white"}
                        display={"flex"} justifyContent={"center"} alignItems={"center"}
                    >
                        {props.unreadNotifications || 0}
                    </Box>
                </Button>
            </HStack>

            {/* Name + company + visibility badge — single line 27-03-2026 */}
            <HStack w={"100%"} mt={1} spacing={1}>
                <Text fontSize={"13px"} fontWeight={600} color={"white"} textTransform={"capitalize"}>
                    {props.personName}
                </Text>
                <Text fontSize={"13px"} fontWeight={300} color={"whiteAlpha.700"}>
                    · {props.companyName}
                </Text>
                {/* Claude: clickable visibility badge → Profile Visibility page 27-03-2026 */}
                {props.visibilityMode && (() => {
                    // Claude: when plan inactive, badge always shows Restricted regardless of DB value 20-04-2026
                    const effectiveMode = (props.isPlanActive === false && props.hasSubscriptionPlan)
                        ? "restricted"
                        : props.visibilityMode;
                    return (
                        <Box
                            ml={1} px={"6px"} py={"1px"} borderRadius={"full"} fontSize={"10px"} fontWeight={500}
                            color={effectiveMode === "open" ? "#155724" : effectiveMode === "targeted" ? "#4A1D96" : "#C62828"}
                            bg={effectiveMode === "open" ? "#D4EDDA" : effectiveMode === "targeted" ? "#EDE9FE" : "#F3F4F6"}
                            cursor="pointer"
                            onClick={() => navigate("/seller/visibility")}
                            _hover={{ filter: "brightness(0.88)" }}
                            transition="filter 0.15s"
                        >
                            {effectiveMode === "open" ? "Open" : effectiveMode === "targeted" ? "Targeted" : "Restricted"}
                        </Box>
                    );
                })()}
            </HStack>
            {/* Claude: negative verification status — condensed single line below name row 15-03-2026 */}
            {(props.verificationStatus === "unverified" || props.verificationStatus === "resubmitted" || props.verificationStatus === "provisional") && (
                <Text w={"100%"} fontSize={"10px"} color={"#FFAB91"} noOfLines={1}>
                    {props.verificationStatus === "unverified"
                        ? "Unverified Profile"
                        : "Profile under review"}
                </Text>
            )}
            {/* Claude: plan limit badge — only when plan assigned but inactive (not when no plan) 20-04-2026 */}
            {props.isPlanActive === false && props.hasSubscriptionPlan && (
                <Box
                    px="6px" py="1px" borderRadius="full" fontSize="10px" fontWeight={500}
                    color="#E65100" bg="#FFF3E0" mt="2px"
                >
                    Plan limit reached. Renew subscription
                </Box>
            )}
        </Flex>
    );
}

export default Jumbotron;